<template>
  <div
    class="file-input"
    :class="{ 'file-input--drop': onArea }"
    @dragenter.prevent="dragenter"
    @dragexit.prevent="dragexit"
    @drop.prevent="drop"
    @dragover.prevent
    @dragleave.prevent
  >
    <div class="visually-hidden">
      <label :for="name"></label>
      <input ref="input"
             @change="onChange"
             :name="name"
             type="file"
             multiple />
    </div>
    <div class="file-input__wrap">
      <div class="file-input__text-wrap">
        <h4 class="file-input__title">{{ mainText }}</h4>
        <span v-if="secondaryText" class="file-input__text">{{ secondaryText }}</span>
      </div>
      <div class="file-input__btn-wrap">
        <Button
          class="file-input__btn"
          type="plain"
          @click.native="triggerClick"
          :is-disabled="allFilesFilled"
          :is-native-disabled="allFilesFilled"
        >{{ buttonText || $t('review_get_file') }}</Button
        >
      </div>
    </div>
    <ul v-if="Object.keys(errors).length" class="file-input__errors-list errors-list">
      <li class="errors-list__item" v-for="(error, name) in errors" :key="name">
        {{ error }}
      </li>
    </ul>
    <ul v-if="Object.keys(fileList).length" class="file-input__list file-list">
      <li class="file-list__item" v-for="(obj, name) in fileList" :key="name">
        <img v-if="obj.imgPreview"
             class="file-list__img"
             :src="obj.imgPreview"
             :alt="name" />
        <span class="file-list__name block-ellipsis block-ellipsis-truncate">{{ name }}</span>
        <button class="file-list__remove" @click="remove(name)"></button>
      </li>
    </ul>
  </div>
</template>

<script>
import Button from './Button'
import { mimeTypesList } from '@/config'

export default {
  name: 'FileInput',
  components: {
    Button
  },
  data () {
    return {
      fileList: {},
      errors: {},
      timeout: null,
      onArea: false
    }
  },
  props: {
    name: {
      type: String,
      required: true
    },
    formats: {
      type: Array,
      required: true
    },
    maxFilesCount: {
      type: Number,
      required: true
    },
    maxFileSize: {
      type: Number,
      required: true
    },
    buttonText: {
      type: String,
      default: ''
    },
    mainText: {
      type: String,
      required: true,
      default: ''
    },
    secondaryText: {
      type: String,
      default: ''
    }
  },
  computed: {
    currentFiles () {
      return Object.values(this.fileList).map(item => item.file)
    },
    currentFilesCount () {
      return Object.keys(this.fileList).length
    },
    errorsTimeout () {
      const timeForOneError = 5000
      const maxTimeForErrors = 20000
      const currentTimeForErrors = Object.keys(this.errors).length * timeForOneError

      if (currentTimeForErrors <= maxTimeForErrors) return currentTimeForErrors
      return maxTimeForErrors
    },
    allFilesFilled () {
      return this.currentFilesCount === this.maxFilesCount
    }
  },
  methods: {
    onChange () {
      Array.from(this.$refs.input.files).forEach(item => {
        if (this.validateFile(item)) {
          if (this.isImageFile(item.type)) {
            const reader = new FileReader()
            reader.onload = e => {
              this.$set(this.fileList, item.name, { file: item, imgPreview: e.target.result })
            }
            reader.readAsDataURL(item)
          } else {
            this.$set(this.fileList, item.name, { file: item, imgPreview: null })
          }
        }
      })
    },
    triggerClick () {
      this.$refs.input.click()
    },
    remove (name) {
      const clonedFiles = { ...this.fileList }
      delete clonedFiles[name]

      this.fileList = clonedFiles
    },
    dragenter () {
      if (!this.onArea) {
        this.onArea = true
      }
    },
    dragexit () {
      if (this.onArea) {
        this.onArea = false
      }
    },
    drop (event) {
      if (this.currentFilesCount === this.maxFilesCount) return

      this.onArea = false
      this.$refs.input.files = event.dataTransfer.files
      this.onChange()
    },
    validateFile (file) {
      if (this.currentFilesCount === this.maxFilesCount) {
        if (!this.errors[this.$t('review_file_max_size_error')]) {
          this.$set(
            this.errors,
            this.$t('review_file_max_size_error'),
            this.$t('review_file_max_size_error')
          )
        }
        return false
      } else if (this.fileList[file.name]) {
        this.$set(
          this.errors,
          file.name,
          this.$t('review_file') + ' (' + file.name + ') ' + this.$t('review_file_duplicate_error')
        )
        return false
      } else if (!this.formats.some(format => mimeTypesList[format] === file.type)) {
        this.$set(
          this.errors,
          file.name,
          this.$t('review_file') + ' (' + file.name + ') ' + this.$t('review_file_format_error')
        )
        return false
      } else if (file.size > this.maxFileSize) {
        this.$set(
          this.errors,
          file.name,
          this.$t('review_file') + ' (' + file.name + ') ' + this.$t('review_file_size_error')
        )
        return false
      }

      return true
    },
    clearFiles () {
      this.$refs.input.value = ''
      this.fileList = {}
      this.errors = {}
    },
    isImageFile (type) {
      return type.split('/')[0] === 'image'
    }
  },
  watch: {
    errors (value) {
      if (Object.keys(value).length) {
        clearTimeout(this.timeout)

        this.timeout = setTimeout(() => {
          this.errors = {}
        }, this.errorsTimeout)
      }
    },
    currentFiles (val) {
      this.$emit('changeFiles', val)
    }
  }
}
</script>

<style scoped lang="sass">

.file-input
  position: relative
  width: 100%
  padding: 25px 30px
  border: 1px dashed #E7E7E7
  border-radius: 3px

  &--drop
    border-color: $text-color

.file-input__wrap
  display: flex
  flex-flow: column

  +mmin($tablet)
    flex-flow: row nowrap
    align-items: center

.file-input__text-wrap
  width: 100%

  +mmin($tablet)
    width: 65%

.file-input__btn-wrap
  width: 100%
  margin: 20px 0 0

  +mmin($tablet)
    width: max-content
    margin: 0 0 0 auto

.file-input__title
  margin: 0
  font-size: 18px
  font-weight: 500

.file-input__text
  display: block
  margin: 10px 0 0
  font-size: 14px
  color: $placeholder-color

.file-input__btn
  flex-shrink: 0

.errors-list
  +list-unstyled
  width: 100%
  padding: 20px
  margin: 20px 0 0
  background: rgba(255, 47, 0, 0.1)
  border: 1px solid $main-color
  border-radius: 3px

.errors-list__item
  font-size: 14px
  color: $main-color

  &:not(:first-child)
    margin: 15px 0 0

.file-list
  +list-unstyled
  width: 100%
  margin: 20px 0 0

.file-list__item
  display: flex
  align-items: center
  flex-flow: row nowrap
  width: 100%

  &:not(:first-child)
    margin: 15px 0 0

.file-list__img
  display: block
  width: 40px
  height: 40px
  object-fit: cover
  margin: 0 20px 0 0

.file-list__name
  width: 80%
  -webkit-box-orient: vertical
  -webkit-line-clamp: 2
  color: $placeholder-color

  +mmin($tablet)
    -webkit-line-clamp: 1

.file-list__remove
  cursor: pointer
  position: relative
  display: block
  width: 15px
  height: 15px
  opacity: .6
  border: 0
  padding: 0
  background-color: transparent
  margin: 0 0 0 auto

  &:hover
    opacity: 1

  &:before,
  &:after
    content: ''
    position: absolute
    top: 0
    left: 50%
    height: 15px
    width: 2px
    background-color: $text-color

  &:before
    transform: rotate(45deg)

  &:after
    transform: rotate(-45deg)
</style>
